import { useTranslate } from "../../../../customHooks";
import { composeValidators, isAlphaNumeric, isRequired, maxLength } from "../../../../utils/validate";
import { ApplicationConfigManager, SupportedRegion } from "../../../../types";
import { useGetRegionsQuery } from "../../../../redux/services/regions-service";
import { useEffect, useState } from "react";
import { find } from "lodash";
import { Input, RadioGroup, Select } from "@panwds/react-form";
import { useWatch } from "react-hook-form";
import { useFilteredPermissions } from "../../../../customHooks/useFilteredPermissions";
import { Banner, Link } from "@panwds/react-ui";
import { FormattedMessage } from "react-intl";

const Index = (props: any) => {
    const { setValue, control } = props
    const translate = useTranslate();
    const { filteredChangeProtectionOptions } = useFilteredPermissions();

    const hasGFA = find(filteredChangeProtectionOptions, { value: "GlobalFirewallAdmin" });

    if (hasGFA) {
        hasGFA.disabled = true;
    } else {
        filteredChangeProtectionOptions.push({
            children: "Global Firewall Admin",
            value: "GlobalFirewallAdmin",
            disabled: true
        })
    }

    const initChangeProtectionValue = filteredChangeProtectionOptions
        .filter((option: any) => option.value === "GlobalFirewallAdmin")
        .map((option: any) => option.value);

    const regionChanged = useWatch({
        control,
        name: "Region"
    });

    const currentTier = useWatch({
        control,
        name: "Tier"
    });

    const { data: zones, error, isLoading: isZonesLoading, refetch: refetchZones } = useGetRegionsQuery(regionChanged, {
        skip: !regionChanged,
    });

    useEffect(() => {
        setValue("CustomerZoneIdList", undefined);
        if (zones) {
            refetchZones(); // Refetch zones when region changes if the query has been initiated
        }
    }, [regionChanged]);

    const regionItems = ApplicationConfigManager.getInstance().getSupportedRegions().map((region: SupportedRegion) => ({
        label: region.RegionDisplayName,
        value: region.RegionCode
    }));

    return (
        <div className="formLayout">
            <Input
                label={translate(`resources.firewallsV2.firewallName`)}
                name="FirewallName"
                dataMetrics="cloudngfw-firewall-create-name"
                requiredAsterisk
                placeholder="Enter a custom name for this firewall"
                register={{
                    validate: (value) => value === "" ||
                        composeValidators(isRequired,
                            isAlphaNumeric,
                            maxLength)(value)
                }}
            />
            <Select
                items={regionItems}
                label={translate(`resources.firewallsV2.region`)}
                name="Region"
                dataMetrics="cloudngfw-firewall-create-region"
                placeholder="Select a region"
                simpleFormat
                fitToItemWidth
                requiredAsterisk
            />
            <Select
                items={zones}
                label={translate(`resources.firewallsV2.availabilityZoneIDs`)}
                name="CustomerZoneIdList"
                dataMetrics="cloudngfw-firewall-create-availability-zone"
                placeholder="Select one or more Availability Zone IDs"
                simpleFormat
                fitToItemWidth
                requiredAsterisk
                enableMultiSelect
                isLoading={isZonesLoading}
            />
            <Select
                items={filteredChangeProtectionOptions}
                label={translate(`resources.firewallsV2.changeProtection`)}
                name="ChangeProtection"
                dataMetrics="cloudngfw-firewall-create-delete-protection"
                simpleFormat
                fitToItemWidth
                requiredAsterisk
                enableMultiSelect
                defaultValue={initChangeProtectionValue}
                tooltip={translate(`resources.firewallsV2.fwCreate.changeProtectionTooltip`)}
            />
            <RadioGroup horizontal
                label={translate(`resources.firewallsV2.tier.title`)}
                name="Tier"
                dataMetrics={"cloudngfw-firewall-edit-tier"}
                defaultValue={"base"}
                requiredAsterisk
                items={[{
                    value: 'base',
                    children: <div className="tw-text-secondary tw-space-x-1 tw-flex tw-items-center">
                        <span>{translate(`resources.firewallsV2.tier.base`)}</span>
                    </div>,
                }, {
                    value: 'standard',
                    children: <div className="tw-text-secondary tw-space-x-1 tw-flex tw-items-center">
                        <span>{translate(`resources.firewallsV2.tier.standard`)}</span>
                    </div>,
                }, {
                    value: 'premium',
                    children: <div className="tw-text-secondary tw-space-x-1 tw-flex tw-items-center">
                        <span>{translate(`resources.firewallsV2.tier.premium`)}</span>
                    </div>
                }]}
            />
            <Banner
                type="inline"
                showIcon>
                <span>
                    {currentTier === 'premium' ? (
                        <FormattedMessage
                            id="resources.firewallsV2.tier.premiumCreateInfo"
                            values={{
                                muchLargerPolicySetsLink: (
                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/cloud-ngfw-for-aws-limits-and-quotas?otp=concept-rz5_1v5_lbc#concept-rz5_1v5_lbc">
                                        much larger policy sets
                                    </Link>
                                ),
                                higherPriceLink: (
                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/pricing">
                                        higher price
                                    </Link>
                                )
                            }}
                        />
                    ) : currentTier === 'standard' ? (
                        <FormattedMessage
                            id="resources.firewallsV2.tier.standardCreateInfo"
                            values={{
                                largerPolicySetsLink: (
                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/cloud-ngfw-for-aws-limits-and-quotas?otp=concept-rz5_1v5_lbc#concept-rz5_1v5_lbc">
                                        larger policy sets
                                    </Link>
                                ),
                                noAdditionalPriceLink: (
                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/pricing">
                                        no additional price
                                    </Link>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="resources.firewallsV2.tier.baseCreateInfo"
                            values={{
                                smallerPolicySetsLink: (
                                    <Link external href="https://docs.paloaltonetworks.com/cloud-ngfw-aws/reference/cloud-ngfw-for-aws-limits-and-quotas?otp=concept-gcm_1v5_lbc#concept-gcm_1v5_lbc">
                                        smaller policy sets
                                    </Link>
                                )
                            }}
                        />
                    )}
                </span>
            </Banner>
        </div>
    );
};
export default Index;
